import * as zod from 'zod';

import { OnRobotDualQuickChangerCommand } from '@sb/integrations/implementations/onRobot/OnRobotDualQuickChanger/types';
import {
  COMMAND_FORCE_N_DEFAULT,
  COMMAND_KIND_DEFAULT,
  COMMAND_SCREW_LENGTH_MM_DEFAULT,
  COMMAND_SHANK_POSITION_MM_DEFAULT,
  COMMAND_TORQUE_NM_DEFAULT,
} from '@sb/integrations/implementations/onRobot/OnRobotScrewdriver/constants';
import { OnRobotScrewdriverCommandKind } from '@sb/integrations/implementations/onRobot/OnRobotScrewdriver/types';
import type { OnRobotVGP20ChannelCommand } from '@sb/integrations/implementations/onRobot/OnRobotVGP20/types';
import { schunkEGxActuateGripperStepGenerateCommand } from '@sb/integrations/implementations/schunk/SchunkEGx/step-wizard/schunkEGxActuateGripperStepGenerateCommand';
import type {
  DeviceConfiguration,
  EquipmentItem,
} from '@sb/integrations/types';
import { isEndEffector } from '@sb/integrations/utils';
import { getEndEffector } from '@sb/integrations/utils/frontend/util';
import { ActionRequiredError, Step } from '@sb/remote-control/types';

import {
  GRIPPER_DIAMETER_MM_DEFAULT,
  GRIPPER_FORCE_NEWTONS_DEFAULT,
  GRIPPER_FORCE_PERCENT_DEFAULT,
  GRIPPER_GRIP_KIND_DEFAULT,
  GRIPPER_TOLERANCE_METERS_DEFAULT,
} from '../constants';
import type { ActuateGripperArguments } from '../steps/ActuateGripper/Arguments';

const WAIT_FOR_GRIP_DEFAULT = true;
const IS_FLEX_GRIP_DEFAULT = false;

export namespace ActuateGripperStepDatabase {
  export const name = 'Actuate gripper';
  export const description = 'Open or close the gripper to pick up objects';
  export const deviceKinds = [
    'CustomGripper',
    'OnRobot2FG7',
    'OnRobot2FG14',
    'OnRobot3FG15',
    'OnRobotDualQuickChanger',
    'OnRobotScrewdriver',
    'OnRobotVGP20',
    'Robotiq2F',
    'RobotiqEPick',
    'SchunkEGx',
  ] as const;
  export const librarySection = Step.LibrarySection.Basic;
  export const librarySort = '2';
  export const argumentKind = 'ActuateGripper';

  export const Arguments = zod.object({
    argumentKind: zod.literal(argumentKind),
    // deviceID to select which gripper to use from equipment
    deviceID: zod.string().optional(),
    // deviceKind preserved alongside deviceID so cross-robot imports can
    // remap to a same-kind gripper on the target.
    deviceKind: zod.string().optional(),
    // for dual gripper
    selectedGripper: zod.enum(['primary', 'secondary']).default('primary'),
    diameterMM: zod.number().default(GRIPPER_DIAMETER_MM_DEFAULT),
    forceNewtons: zod.number().default(GRIPPER_FORCE_NEWTONS_DEFAULT),
    gripKind: zod
      .enum(['inward', 'outward'])
      .default(GRIPPER_GRIP_KIND_DEFAULT),
    payloadKg: zod.number().default(0),
    targetDiameterToleranceMeters: zod
      .number()
      .default(GRIPPER_TOLERANCE_METERS_DEFAULT),
    waitForGripToContinue: zod.boolean().default(WAIT_FOR_GRIP_DEFAULT),
    isFlexGrip: zod.boolean().default(IS_FLEX_GRIP_DEFAULT),
    forcePercent: zod.number().default(GRIPPER_FORCE_PERCENT_DEFAULT),
    speed: zod.number().optional(),
    // for RobotiqEPick
    mode: zod.enum(['auto', 'advanced']).optional(),
    // for Schunk EGK
    isSoftGrip: zod.boolean().optional(),
    // for Schunk EGU with GPE
    isStrongGrip: zod.boolean().optional(),
    // for Schunk with GPE
    activateGPE: zod.boolean().optional(),
    stopRoutineOnFailure: zod.boolean().optional(),
    actionID: zod.string().nullable().optional(),
    // for OnRobotVGP20
    oneCommandKind: zod.enum(['RELEASE', 'GRIP', 'IDLE']).optional(),
    twoCommandKind: zod.enum(['RELEASE', 'GRIP', 'IDLE']).optional(),
    threeCommandKind: zod.enum(['RELEASE', 'GRIP', 'IDLE']).optional(),
    fourCommandKind: zod.enum(['RELEASE', 'GRIP', 'IDLE']).optional(),
    overallSuctionPercentage: zod.number().optional(),
    // for OnRobotScrewdriver
    commandKind: OnRobotScrewdriverCommandKind.default(COMMAND_KIND_DEFAULT),
    targetTorque: zod.number().default(COMMAND_TORQUE_NM_DEFAULT), // STORED AS Nm (0.1-5)
    targetForce: zod.number().default(COMMAND_FORCE_N_DEFAULT), // STORED AS N 18 to 30 N
    screwLength: zod.number().default(COMMAND_SCREW_LENGTH_MM_DEFAULT), // STORED AS MM (0-35)
    shankPosition: zod.number().default(COMMAND_SHANK_POSITION_MM_DEFAULT), // 0 to 55mm
  });

  export type Arguments = zod.infer<typeof Arguments>;

  // eslint-disable-next-line no-inner-declarations
  function convertConfigurationArgs(
    stepID: string,
    equipment: EquipmentItem[],
    configuration: Arguments,
  ): ActuateGripperArguments {
    let endEffector: DeviceConfiguration | undefined;

    if (configuration.deviceID) {
      const selectedDevice = equipment.find(
        (item) => item.id === configuration.deviceID,
      );

      if (selectedDevice && isEndEffector(selectedDevice.config.kind)) {
        endEffector = selectedDevice.config;
      } else if (selectedDevice) {
        throw new ActionRequiredError({
          kind: 'missingEquipment',
          message: `Selected device "${selectedDevice.config.name || selectedDevice.id}" is not a gripper. Please select a gripper in the Equipment Manager.`,
        });
      }
    }

    // Fall back to finding any actuating gripper if deviceID wasn't set or device wasn't found
    // The device will not be found in the recursive quick changer case, but there will only be one item
    // in equipment in that scenario, so it will be picked up here
    if (!endEffector) {
      const deviceConfigs = equipment.map((item) => item.config);
      endEffector = getEndEffector(deviceConfigs);
    }

    if (!endEffector) {
      const error = new Error(
        'Attach a gripper to utilize the Actuate Gripper step',
      );

      error.name = 'EquipmentNotFound';
      throw error;
    }

    switch (endEffector.kind) {
      case 'OnRobot2FG7': {
        return {
          gripperCommand: {
            kind: 'OnRobot2FG7Command',
            gripKind: configuration.gripKind,
            targetForce: configuration.forceNewtons,
            targetDiameter: configuration.diameterMM / 1000,
            waitForGripToContinue: configuration.waitForGripToContinue,
            targetDiameterTolerance:
              configuration.targetDiameterToleranceMeters,
            // This is hard coded to a reasonable minimum, but could be added to FE
            // and wired through
            targetSpeed: 0.95,
          },
          stopRoutineOnFailure: configuration.stopRoutineOnFailure ?? true,
          retry: configuration.stopRoutineOnFailure ?? true,
          deviceID: configuration.deviceID,
        };
      }

      case 'OnRobot2FG14': {
        return {
          gripperCommand: {
            kind: 'OnRobot2FG14Command',
            gripKind: configuration.gripKind,
            targetForce: configuration.forceNewtons,
            targetDiameter: configuration.diameterMM / 1000,
            waitForGripToContinue: configuration.waitForGripToContinue,
            targetDiameterTolerance:
              configuration.targetDiameterToleranceMeters,
            // This is hard coded to a reasonable minimum, but could be added to FE
            // and wired through
            targetSpeed: 0.95,
          },
          stopRoutineOnFailure: configuration.stopRoutineOnFailure ?? true,
          retry: configuration.stopRoutineOnFailure ?? true,
          deviceID: configuration.deviceID,
        };
      }

      case 'OnRobot3FG15': {
        return {
          gripperCommand: {
            kind: 'OnRobot3FG15Command',
            gripKind: configuration.gripKind,
            targetForce: configuration.forceNewtons,
            targetDiameter: configuration.diameterMM / 1000,
            targetDiameterTolerance:
              configuration.targetDiameterToleranceMeters,
            isFlexGrip: configuration.isFlexGrip,
            waitForGripToContinue: configuration.waitForGripToContinue,
          },
          stopRoutineOnFailure: configuration.stopRoutineOnFailure ?? true,
          retry: configuration.stopRoutineOnFailure ?? true,
          deviceID: configuration.deviceID,
        };
      }

      case 'OnRobotDualQuickChanger': {
        const selectedGripperConfig =
          endEffector.grippers[configuration.selectedGripper];

        if (selectedGripperConfig.kind === 'NoGripper') {
          throw new ActionRequiredError({
            kind: 'missingEquipment',
            message: `The ${configuration.selectedGripper} gripper is not configured`,
          });
        }

        const { gripperCommand } = convertConfigurationArgs(
          stepID,
          [
            {
              id: `${stepID}-${configuration.selectedGripper}`,
              config: selectedGripperConfig,
              isEnabled: false,
            },
          ],
          configuration,
        );

        const parsedSubCommand =
          OnRobotDualQuickChangerCommand.shape.command.safeParse(
            gripperCommand,
          );

        if (!parsedSubCommand.success) {
          throw new ActionRequiredError({
            kind: 'missingEquipment',
            message: `Sub-command given to Dual quick changer is not supported: ${parsedSubCommand.error.toString()}`,
          });
        }

        return {
          gripperCommand: {
            kind: 'OnRobotDualQuickChangerCommand',
            active: configuration.selectedGripper,
            command: parsedSubCommand.data,
          },
          stopRoutineOnFailure: configuration.stopRoutineOnFailure ?? true,
          retry: configuration.stopRoutineOnFailure ?? true,
          deviceID: configuration.deviceID,
        };
      }

      case 'Robotiq2F': {
        if (endEffector.kind !== 'Robotiq2F') {
          throw new TypeError('Expected Robotiq 2F configuration');
        }

        const targetSpeed =
          configuration.speed ?? endEffector.defaultSpeedMetersPerSecond;

        const targetForce =
          configuration.forceNewtons ?? endEffector.defaultForceNewtons;

        return {
          gripperCommand: {
            kind: 'Robotiq2FCommand',
            controlKind: 'move',
            targetGripWidth: configuration.diameterMM / 1000,
            targetSpeed,
            targetForce,
            waitForGripToContinue: configuration.waitForGripToContinue,
            targetDiameterTolerance:
              configuration.targetDiameterToleranceMeters,
          },
          stopRoutineOnFailure: configuration.stopRoutineOnFailure ?? true,
          retry: configuration.stopRoutineOnFailure ?? true,
          deviceID: configuration.deviceID,
        };
      }

      case 'RobotiqEPick': {
        if (endEffector.kind !== 'RobotiqEPick') {
          throw new TypeError('Expected Robotiq EPick configuration');
        }

        const controlKind = configuration.waitForGripToContinue
          ? 'grip'
          : 'release';

        return {
          gripperCommand: {
            kind: 'RobotiqEPickCommand',
            controlKind,
            mode: configuration.mode ?? 'auto',
            forcePercent:
              controlKind === 'grip' ? configuration.forcePercent : undefined,
          },
          stopRoutineOnFailure: configuration.stopRoutineOnFailure ?? true,
          retry: configuration.stopRoutineOnFailure ?? true,
          deviceID: configuration.deviceID,
        };
      }

      case 'SchunkEGx': {
        const stopOnFailure = configuration.stopRoutineOnFailure ?? false;

        return {
          gripperCommand: schunkEGxActuateGripperStepGenerateCommand(
            endEffector,
            configuration,
          ),
          stopRoutineOnFailure: stopOnFailure,
          retry: stopOnFailure,
          deviceID: configuration.deviceID,
        };
      }

      case 'CustomGripper':
        return {
          gripperCommand: {
            kind: 'CustomGripperCommand',
            actionID: configuration.actionID ?? '',
          },
          stopRoutineOnFailure: configuration.stopRoutineOnFailure ?? true,
          deviceID: configuration.deviceID,
        };

      case 'OnRobotVGP20': {
        const getChannelCommand = (
          channel: keyof {
            one: OnRobotVGP20ChannelCommand;
            two: OnRobotVGP20ChannelCommand;
            three: OnRobotVGP20ChannelCommand;
            four: OnRobotVGP20ChannelCommand;
          },
        ): OnRobotVGP20ChannelCommand => {
          const commandKind = configuration[`${channel}CommandKind`] ?? 'IDLE';

          const overallSuctionPercentage =
            configuration.overallSuctionPercentage ?? 1;

          return {
            commandKind: commandKind as 'GRIP' | 'RELEASE' | 'IDLE',
            suctionPercentage:
              commandKind === 'GRIP' ? overallSuctionPercentage : 0,
          };
        };

        return {
          gripperCommand: {
            kind: 'OnRobotVGP20Command',
            suctionActions: {
              one: getChannelCommand('one'),
              two: getChannelCommand('two'),
              three: getChannelCommand('three'),
              four: getChannelCommand('four'),
            },
          },
          stopRoutineOnFailure: configuration.stopRoutineOnFailure ?? true,
          deviceID: configuration.deviceID,
        };
      }

      case 'OnRobotScrewdriver': {
        return {
          gripperCommand: {
            kind: 'OnRobotScrewdriverCommand',
            commandKind: configuration.commandKind,
            targetForce: configuration.targetForce,
            screwLength: configuration.screwLength,
            targetTorque: configuration.targetTorque,
            shankPosition: configuration.shankPosition,
          },
          stopRoutineOnFailure: configuration.stopRoutineOnFailure ?? true,
          deviceID: configuration.deviceID,
        };
      }

      default: {
        throw new ActionRequiredError({
          kind: 'missingEquipment',
          message: `Gripper ${endEffector.kind} is not supported for this step`,
        });
      }
    }
  }

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
    equipment,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    return {
      ...stepData,
      stepKind: 'ActuateGripper',
      args: convertConfigurationArgs(stepData.id, equipment, args),
    };
  };

  export const getStepDescription: Step.GetStepDescription = ({
    stepConfiguration: { args },
    includeStepName,
    distanceUnitInfo,
    equipment,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      return null;
    }

    // Check if this is a vacuum gripper (VGP20) or screwdriver
    let isVacuumGripper = false;
    let isScrewdriver = false;
    let gripperName: string | undefined;

    if (equipment && args.deviceID) {
      const selectedGripper = equipment.find(
        (item) => item.id === args.deviceID,
      );

      isVacuumGripper = selectedGripper?.config.kind === 'OnRobotVGP20';
      isScrewdriver = selectedGripper?.config.kind === 'OnRobotScrewdriver';
      gripperName = selectedGripper?.config.name;
    } else if (equipment) {
      // If no deviceID, check if the enabled gripper is VGP20 or screwdriver
      const enabledGripper = equipment.find(
        (item) => isEndEffector(item.config.kind) && item.isEnabled,
      );

      isVacuumGripper = enabledGripper?.config.kind === 'OnRobotVGP20';
      isScrewdriver = enabledGripper?.config.kind === 'OnRobotScrewdriver';
      gripperName = enabledGripper?.config.name;
    }

    // For vacuum grippers, use a description with gripper name
    if (isVacuumGripper) {
      if (includeStepName) {
        if (gripperName) {
          return `Actuate vacuum with ${gripperName}`;
        }

        return 'Actuate vacuum';
      }

      if (gripperName) {
        return `vacuum with ${gripperName}`;
      }

      return 'vacuum';
    }

    // For screwdrivers, use a description based on command kind
    if (isScrewdriver) {
      const commandKind = args.commandKind as
        | 'TIGHTEN'
        | 'LOOSEN'
        | 'PICKUP'
        | 'STOP'
        | 'SET_SHANK_POSITION'
        | undefined;

      const commandDescriptions: Record<
        'TIGHTEN' | 'LOOSEN' | 'PICKUP' | 'STOP' | 'SET_SHANK_POSITION',
        string
      > = {
        TIGHTEN: 'Tighten screw',
        LOOSEN: 'Loosen screw',
        PICKUP: 'Pick up screw',
        STOP: 'Stop',
        SET_SHANK_POSITION: 'Adjust z-axis position',
      };

      const actionDescription =
        commandKind && commandKind in commandDescriptions
          ? commandDescriptions[commandKind].toLowerCase()
          : 'control screwdriver';

      if (includeStepName) {
        if (gripperName) {
          return `Actuate screwdriver with ${gripperName}: ${actionDescription}`;
        }

        return `Actuate screwdriver: ${actionDescription}`;
      }

      if (gripperName) {
        return `screwdriver with ${gripperName}: ${actionDescription}`;
      }

      return `screwdriver: ${actionDescription}`;
    }

    // TODO: we want to change the step name to "Actuate Primary Gripper"
    // if we're using a dual gripper - that check needs to happen elsewhere
    // as this only affects the description text

    const { gripKind, diameterMM, waitForGripToContinue, isFlexGrip } = args;

    const finalString = `to ${distanceUnitInfo ? distanceUnitInfo.toString(diameterMM / 1000) : `${diameterMM} millimeters`}`;

    const sentenceBody = [
      isFlexGrip || waitForGripToContinue ? 'to grasp object' : false,
      gripKind === 'inward' ? false : 'moving outward',
      isFlexGrip ? false : finalString,
    ]
      .filter((item) => item !== false)
      .join(' ');

    if (includeStepName) {
      const gripperPrefix = gripperName ? `${gripperName} ` : '';

      return `Actuate ${gripperPrefix}gripper ${sentenceBody}`;
    }

    // Include gripper name in external descriptions as well
    const gripperPrefix = gripperName ? `${gripperName} ` : '';

    return gripperPrefix ? `${gripperPrefix}${sentenceBody}` : sentenceBody;
  };
}

ActuateGripperStepDatabase satisfies Step.StepKindInfo;
